import { overlay } from 'overlay-kit';
import { useState } from 'react';

import { useScrapingJobs } from '../hooks/use-scraping-jobs';
import { GoogleSearchScrapingDialog } from './GoogleSearchScrapingDialog/GoogleSearchScrapingDialog';
import { GoogleSearchScrapingTable } from './GoogleSearchScrapingTable/GoogleSearchScrapingTable';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { PageNavigationView } from '@/shared/components/page-navigation';
import { Spacer } from '@/shared/components/spacer';

const ITEM_PER_PAGE = 10;

export function GoogleSearchScrapingList() {
  const [platformFilter, setPlatformFilter] = useState<SOURCE_PLATFORM_TYPE | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [jobTypeFilter, setJobTypeFilter] = useState<'all' | 'onetime' | 'periodic'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'IN_PROGRESS' | 'DONE'>('all');
  const [ownerSearch, setOwnerSearch] = useState<string>('');

  const {
    jobs,
    totalCount,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext,
    currentPage,
    setPage,
    isLoading,
    isFetching,
  } = useScrapingJobs({
    type: 'GOOGLE_SEARCH',
    limit: ITEM_PER_PAGE,
    platform: platformFilter,
    keyword: searchKeyword,
    jobType: jobTypeFilter,
    status: statusFilter,
    owner: ownerSearch,
  });

  const startIndex = totalCount > 0 ? (currentPage - 1) * ITEM_PER_PAGE + 1 : 0;
  const endIndex = Math.min(startIndex + ITEM_PER_PAGE - 1, totalCount);

  const handleCreateNew = () => {
    overlay.open(({ isOpen, close, unmount }) => {
      return (
        <GoogleSearchScrapingDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={() => {}}
          onExit={unmount}
        />
      );
    });
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    setPage(1); // Reset to first page when searching
  };

  const handleOwnerSearch = (owner: string) => {
    setOwnerSearch(owner);
    setPage(1);
  };

  const handleJobTypeFilterChange = (value: 'all' | 'onetime' | 'periodic') => {
    setJobTypeFilter(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: 'all' | 'IN_PROGRESS' | 'DONE') => {
    setStatusFilter(value);
    setPage(1);
  };

  // Show initial loading state only on first load
  if (isLoading) {
    return (
      <div>
        <GoogleSearchScrapingTable
          scrapingJobs={[]}
          total={0}
          isFetching={true}
          onSearch={handleSearch}
          onOwnerSearch={handleOwnerSearch}
          onJobTypeFilterChange={handleJobTypeFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          jobTypeFilter={jobTypeFilter}
          statusFilter={statusFilter}
          onCreateNew={handleCreateNew}
          platform={platformFilter}
          onPlatformChange={setPlatformFilter}
        />
      </div>
    );
  }

  return (
    <div>
      <GoogleSearchScrapingTable
        scrapingJobs={jobs}
        total={totalCount}
        isFetching={isFetching}
        onSearch={handleSearch}
        onOwnerSearch={handleOwnerSearch}
        onJobTypeFilterChange={handleJobTypeFilterChange}
        onStatusFilterChange={handleStatusFilterChange}
        jobTypeFilter={jobTypeFilter}
        statusFilter={statusFilter}
        onCreateNew={handleCreateNew}
        platform={platformFilter}
        onPlatformChange={setPlatformFilter}
      />
      <hr />
      <PageNavigationView
        startIndex={startIndex}
        endIndex={endIndex}
        totalCount={totalCount}
        onPrevious={goToPrevious}
        onNext={goToNext}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
      />
      <Spacer size={36} />
    </div>
  );
}
